import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, Search, RefreshCw } from 'lucide-react';
import { getAllHotels } from '@/lib/platformAdminApi';
import { toast } from 'sonner';

const getTierBadgeClass = (tier) => {
  const tierMap = {
    'Bronze': 'bg-[hsl(var(--tier-bronze))]/15 text-[hsl(var(--tier-bronze))] border border-[hsl(var(--tier-bronze))]/30',
    'Silver': 'bg-[hsl(var(--tier-silver))]/15 text-[hsl(var(--tier-silver))] border border-[hsl(var(--tier-silver))]/30',
    'Gold': 'bg-[hsl(var(--tier-gold))]/15 text-[hsl(var(--tier-gold))] border border-[hsl(var(--tier-gold))]/30',
    'Platinum': 'bg-[hsl(var(--tier-platinum))]/15 text-[hsl(var(--tier-platinum))] border border-[hsl(var(--tier-platinum))]/30',
    'Diamond': 'bg-[hsl(var(--tier-diamond))]/15 text-[hsl(var(--tier-diamond))] border border-[hsl(var(--tier-diamond))]/30',
    'Excellent': 'bg-[hsl(var(--tier-excellent))]/15 text-[hsl(var(--tier-excellent))] border border-[hsl(var(--tier-excellent))]/30'
  };
  return tierMap[tier] || 'bg-gray-100 text-gray-600';
};

export const HotelRegistryTable = () => {
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchHotels = async () => {
    setLoading(true);
    try {
      const response = await getAllHotels();
      if (response.hotels) {
        setHotels(response.hotels);
        setFilteredHotels(response.hotels);
      }
    } catch (error) {
      console.error('Error fetching hotels:', error);
      toast.error('Failed to load hotels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredHotels(hotels);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredHotels(
        hotels.filter(
          (hotel) =>
            hotel.hotel_name?.toLowerCase().includes(query) ||
            hotel.hotel_id?.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, hotels]);

  return (
    <Card className="card-hover-lift" data-testid="hotel-registry-table">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="heading-font">Hotel Registry</CardTitle>
            <CardDescription>All hotels enrolled in the getbitsy.ai platform</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchHotels}
            disabled={loading}
            data-testid="refresh-hotels-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by hotel name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="hotel-search-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-lg">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          ) : filteredHotels.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-muted-foreground mb-4">
                {searchQuery ? 'No hotels match your search' : 'No hotels enrolled yet'}
              </p>
              {!searchQuery && (
                <Button variant="outline" size="sm">
                  Enroll First Hotel
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Hotel Name</TableHead>
                  <TableHead>Hotel ID</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead className="text-right">Tier Score</TableHead>
                  <TableHead className="text-right">Staked HERMES</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHotels.map((hotel) => (
                  <TableRow key={hotel.hotel_id} className="table-row-hover">
                    <TableCell className="font-medium">{hotel.hotel_name || 'N/A'}</TableCell>
                    <TableCell>
                      <code className="mono text-xs bg-muted px-2 py-1 rounded">
                        {hotel.hotel_id}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge className={`tier-badge-base ${getTierBadgeClass(hotel.tier)}`}>
                        {hotel.tier || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {hotel.tier_score || 0}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {((hotel.staked_amount || 0) / Math.pow(10, 18)).toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/admin/blockchain?hotelId=${hotel.hotel_id}`, '_blank')}
                        data-testid={`view-hotel-${hotel.hotel_id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Summary Footer */}
        {!loading && filteredHotels.length > 0 && (
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredHotels.length} of {hotels.length} hotel{hotels.length !== 1 ? 's' : ''}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HotelRegistryTable;
